import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, Circle, Loader2, MoreHorizontal, XCircle, Archive, Pencil, Flame, Repeat, Link2, AlarmClock } from "lucide-react";
import { toast } from "sonner";

import { runService } from "@/services/runService";
import { taskCategoryLabel } from "@/config/runOptions";
import { fmtDateTime } from "@/lib/datetime";
import { Badge } from "@/components/ui/badge";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

const relPath = (r) => {
  if (!r) return null;
  if (r.type === "transaction") return `/transactions/${r.id}`;
  if (r.contact_id) return `/crm/${r.contact_id}`;
  return null;
};

export const TaskItem = ({ task: t, onChanged, onEdit }) => {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const done = t.status === "completed";
  const cancelled = t.status === "cancelled";
  const pending = t.status === "pending";

  const run = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); onChanged?.(); }
    catch (e) { toast.error(e.message || "Action failed"); }
    finally { setBusy(false); }
  };

  const rp = relPath(t.related);

  return (
    <div className="flex items-start gap-3 rounded-lg border border-border bg-card px-4 py-3" data-testid={`task-item-${t.id}`}>
      <button
        onClick={() => pending && run(() => runService.completeTask(t.id), "Task completed")}
        disabled={!pending || busy}
        className="mt-0.5 shrink-0 text-muted-foreground transition-colors hover:text-emerald-600 disabled:cursor-default"
        data-testid={`task-complete-${t.id}`}
        aria-label={done ? "Completed" : "Complete task"}
      >
        {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : done ? <CheckCircle2 className="h-5 w-5 text-emerald-600" /> : <Circle className="h-5 w-5" />}
      </button>

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className={`text-sm font-medium ${done || cancelled ? "text-muted-foreground line-through" : "text-foreground"}`} data-testid={`task-title-${t.id}`}>{t.title}</span>
          {t.priority === "high" && pending && <Badge variant="outline" className="gap-0.5 border-red-300 text-[10px] text-red-700"><Flame className="h-2.5 w-2.5" /> High</Badge>}
          <Badge variant="secondary" className="text-[10px]">{taskCategoryLabel(t.category)}</Badge>
          {t.is_recurring && <Badge variant="outline" className="gap-0.5 text-[10px]"><Repeat className="h-2.5 w-2.5" /> {t.recurrence_rule}</Badge>}
          {cancelled && <Badge variant="outline" className="text-[10px] text-muted-foreground">Cancelled</Badge>}
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
          {t.due_at && (
            <span className={`flex items-center gap-1 ${t.is_overdue ? "font-medium text-red-600" : ""}`}>
              {t.is_overdue && <AlarmClock className="h-3 w-3" />}{t.is_overdue ? "Overdue · " : "Due "}{fmtDateTime(t.due_at)}
            </span>
          )}
          {!t.due_at && pending && <span>No due date</span>}
          {t.related?.label && (
            <button className="flex items-center gap-1 hover:text-accent disabled:hover:text-muted-foreground" disabled={!rp} onClick={() => rp && navigate(rp)} data-testid={`task-related-${t.id}`}>
              <Link2 className="h-3 w-3" /> {t.related.label}
            </button>
          )}
          {t.description && <span className="truncate">· {t.description}</span>}
        </div>
      </div>

      {pending && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="mt-0.5 shrink-0 rounded-md border border-border p-1.5 hover:bg-secondary" data-testid={`task-actions-${t.id}`} aria-label="Task actions">
              <MoreHorizontal className="h-4 w-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-44">
            <DropdownMenuItem onClick={() => onEdit?.(t)} data-testid={`task-edit-${t.id}`}><Pencil className="h-4 w-4" /> Edit</DropdownMenuItem>
            <DropdownMenuItem onClick={() => run(() => runService.cancelTask(t.id), "Task cancelled")} className="text-destructive" data-testid={`task-cancel-${t.id}`}><XCircle className="h-4 w-4" /> Cancel</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
      {(done || cancelled) && (
        <button onClick={() => run(() => runService.archiveTask(t.id), "Task archived")} className="mt-0.5 shrink-0 rounded-md border border-border p-1.5 text-muted-foreground hover:bg-secondary" data-testid={`task-archive-${t.id}`} aria-label="Archive task">
          <Archive className="h-4 w-4" />
        </button>
      )}
    </div>
  );
};
